import program from './cpmmQuoteProgram.json'
import type { TradeSide } from './userlandTrade'

export type TradePreset = {
  buySlippageBps: number
  sellSlippageBps: number
  maxSlippageBps: number
}

export const DEFAULT_TRADE_PRESETS: TradePreset[] = [
  { buySlippageBps: 100, sellSlippageBps: 150, maxSlippageBps: 300 },
  { buySlippageBps: 200, sellSlippageBps: 250, maxSlippageBps: 500 },
  { buySlippageBps: 500, sellSlippageBps: 750, maxSlippageBps: 1_000 },
]

// The most slippage the server takes, for Instant and for limit orders. It
// lives here, not in userlandTrade, because this module is in the app's boot
// graph (limitOrders imports it) and the trade client must not be.
export const MAX_SLIPPAGE_BPS = 3000

// A typed percent as basis points, held to what the server takes.
export function slippageBpsFromPercent(percent: number): number {
  return Math.max(0, Math.min(MAX_SLIPPAGE_BPS, Math.round(percent * 100)))
}

export function slippageForSide(preset: TradePreset, side: TradeSide): number {
  const configured = side === 'buy' ? preset.buySlippageBps : preset.sellSlippageBps
  return Math.max(0, Math.min(configured, preset.maxSlippageBps))
}

export type LocalTradeQuote = {
  expectedOutRaw: bigint
  minOutRaw: bigint
  priceImpactBps: number
}

export function localTradeQuote(input: {
  side: TradeSide
  offerRaw: bigint
  reserveBaseRaw: string
  reserveQuoteRaw: string
  lpFeeBps: number | null
  protocolFeeBps: number | null
  slippageBps: number
}): LocalTradeQuote | null {
  if (input.offerRaw <= 0n || !isPositiveInteger(input.reserveBaseRaw)) return null
  if (!isPositiveInteger(input.reserveQuoteRaw)) return null
  if (input.lpFeeBps === null || input.protocolFeeBps === null) return null
  if (!validBps(input.lpFeeBps) || !validBps(input.protocolFeeBps)) return null
  if (!Number.isInteger(input.slippageBps) || input.slippageBps < 0 || input.slippageBps >= 10_000)
    return null

  const base = BigInt(input.reserveBaseRaw)
  const quote = BigInt(input.reserveQuoteRaw)
  const reserveIn = input.side === 'buy' ? quote : base
  const reserveOut = input.side === 'buy' ? base : quote
  const result = executeProgram({
    offer: input.offerRaw,
    reserve_in: reserveIn,
    reserve_out: reserveOut,
    lp_fee: BigInt(input.lpFeeBps),
    protocol_fee: BigInt(input.protocolFeeBps),
    slippage: BigInt(input.slippageBps),
  })
  const expectedOutRaw = requiredRegister(result, 'expected_out')
  if (expectedOutRaw <= 0n) return null
  const minOutRaw = requiredRegister(result, 'min_out')
  if (minOutRaw <= 0n) return null

  const priceImpactBps = Number(requiredRegister(result, 'price_impact_bps'))
  return { expectedOutRaw, minOutRaw, priceImpactBps }
}

type Registers = Record<string, bigint>

function executeProgram(inputs: Registers): Registers {
  const registers: Registers = { ...inputs, bps: BigInt(program.bps) }
  for (const instruction of program.operations) {
    const [op, out, left, right] = instruction
    if (!op || !out || !left || !right) throw new Error('invalid CPMM instruction')
    const a = registers[left]
    const b = registers[right]
    if (a === undefined || b === undefined || b < 0n) throw new Error('invalid CPMM program')
    if ((op === 'div_floor' || op === 'div_ceil') && b === 0n)
      throw new Error('invalid CPMM division')
    switch (op) {
      case 'add':
        registers[out] = a + b
        break
      case 'sub':
        registers[out] = a - b
        break
      case 'mul':
        registers[out] = a * b
        break
      case 'div_floor':
        registers[out] = a / b
        break
      case 'div_ceil':
        registers[out] = (a + b - 1n) / b
        break
      default:
        throw new Error(`unknown CPMM op ${op}`)
    }
  }
  return registers
}

function requiredRegister(registers: Registers, name: string): bigint {
  const value = registers[name]
  if (value === undefined) throw new Error(`CPMM program omitted ${name}`)
  return value
}

function validBps(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value < 10_000
}

export function compactTokenAmount(raw: bigint, decimals: number): string {
  if (raw < 0n || !Number.isInteger(decimals) || decimals < 0) return '—'
  const scale = 10n ** BigInt(decimals)
  const units = raw / scale
  const formats: Array<[bigint, string]> = [
    [1_000_000_000n, 'B'],
    [1_000_000n, 'M'],
    [1_000n, 'K'],
  ]
  for (const [divisor, suffix] of formats) {
    if (units < divisor) continue
    return compactWithScale(raw, scale * divisor, suffix)
  }
  return compactWithScale(raw, scale, '')
}

function compactWithScale(raw: bigint, scale: bigint, suffix: string): string {
  const hundredths = (raw * 100n) / scale
  const whole = hundredths / 100n
  const fraction = (hundredths % 100n).toString().padStart(2, '0').replace(/0+$/, '')
  return `${whole}${fraction ? `.${fraction}` : ''}${suffix}`
}

function isPositiveInteger(raw: string): boolean {
  return /^[1-9][0-9]*$/.test(raw)
}
